import { useState, useEffect, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import InstallPWA from '@/components/InstallPWA';
import PushNotificationBanner from '@/components/PushNotificationBanner';
import OneSignal from 'react-onesignal';
import { ThemeProvider } from '@/contexts/ThemeContext';

// Critical pages - load immediately
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Layout from '@/components/Layout';

// Lazy loaded pages - load on demand
const Requests = lazy(() => import('@/pages/Requests'));
const RequestDetail = lazy(() => import('@/pages/RequestDetail'));
const Customers = lazy(() => import('@/pages/Customers'));
const CustomerDetail = lazy(() => import('@/pages/CustomerDetail'));
const Suppliers = lazy(() => import('@/pages/Suppliers'));
const SupplierDetail = lazy(() => import('@/pages/SupplierDetail'));
const Calendar = lazy(() => import('@/pages/Calendar'));
const Team = lazy(() => import('@/pages/Team'));
const Tasks = lazy(() => import('@/pages/Tasks'));
const TaskDetail = lazy(() => import('@/pages/TaskDetail'));
const Stock = lazy(() => import('@/pages/Stock'));
const ProductDetail = lazy(() => import('@/pages/ProductDetail'));
const Sales = lazy(() => import('@/pages/Sales'));
const Collections = lazy(() => import('@/pages/Collections'));
const Goals = lazy(() => import('@/pages/Goals'));
const GoalAnalytics = lazy(() => import('@/pages/GoalAnalytics'));
const FinancialStatus = lazy(() => import('@/pages/FinancialStatus'));
const BackupManagement = lazy(() => import('@/pages/BackupManagement'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const ResponseTimes = lazy(() => import('@/pages/ResponseTimes'));
const Warnings = lazy(() => import('@/pages/Warnings'));
const Messages = lazy(() => import('@/pages/Messages'));
const Payments = lazy(() => import('@/pages/Payments'));
const EmployeeDetail = lazy(() => import('@/pages/EmployeeDetail'));
const Reports = lazy(() => import('@/pages/Reports'));
const EmailRequests = lazy(() => import('@/pages/EmailRequests'));
const EmailAI = lazy(() => import('@/pages/EmailAI'));
const CustomerAnalytics = lazy(() => import('@/pages/CustomerAnalytics'));
const SupplierAnalytics = lazy(() => import('@/pages/SupplierAnalytics'));
const TeamAnalytics = lazy(() => import('@/pages/TeamAnalytics'));

// Loading spinner for lazy loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
  </div>
);

// API base URL - always uses current domain
export const API = `${window.location.origin}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only logout on 401 from our API endpoints, not from external services
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      // Check if it's our API endpoint (not WebSocket or external)
      const isOurApi = requestUrl.includes('/api/') && !requestUrl.includes('ws://');
      
      // Don't logout for auth/me endpoint failures during initial load
      const isAuthMeEndpoint = requestUrl.includes('/api/auth/me') || requestUrl.includes('/api/auth/login');
      
      if (isOurApi && !isAuthMeEndpoint) {
        console.warn('401 Unauthorized - logging out:', requestUrl);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
}

function App() {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  
  const isAuthenticated = !!localStorage.getItem('token');

  // Initialize OneSignal
  useEffect(() => {
    const initOneSignal = async () => {
      try {
        const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
        if (!appId) {
          console.log('OneSignal App ID not configured');
          return;
        }
        
        await OneSignal.init({
          appId: appId,
          allowLocalhostAsSecureOrigin: true,
          serviceWorkerPath: '/OneSignalSDKWorker.js',
          notifyButton: {
            enable: false, // We use custom UI
          },
        });
        
        // If user is logged in, set their external ID for targeted notifications
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          if (userData.id) {
            await OneSignal.login(userData.id);
            console.log('OneSignal: User logged in with ID:', userData.id);
          }
        }
        
        // Listen for notification clicks
        OneSignal.Notifications.addEventListener('click', (event) => {
          const url = event.notification.launchURL;
          if (url) {
            window.location.href = url;
          }
        });
        
        console.log('OneSignal initialized successfully');
      } catch (error) {
        console.error('OneSignal initialization error:', error);
      }
    };
    
    initOneSignal();
  }, []);

  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login setUser={setUser} />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout user={user}>
                      <Dashboard />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Layout user={user}>
                      <Dashboard />
                    </Layout>
                  </PrivateRoute>
                }
            />
            <Route
              path="/requests"
              element={
                <PrivateRoute>
                  <Layout user={user}>
                    <Requests />
                  </Layout>
                </PrivateRoute>
              }
            />
          <Route
            path="/requests/:id"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <RequestDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/email-requests"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <EmailRequests />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/email-ai"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <EmailAI />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Customers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/customers/analytics"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <CustomerAnalytics />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/customers/:customerId"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <CustomerDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Suppliers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/suppliers/analytics"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <SupplierAnalytics />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/suppliers/:supplierId"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <SupplierDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Calendar />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/team"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Team />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Tasks />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/tasks/:id"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <TaskDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/stock"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Stock />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/stock/:id"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <ProductDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/sales"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Sales />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/collections"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Collections />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/goals"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Goals />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/goals/analytics"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <GoalAnalytics />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Reports />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/team/analytics"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <TeamAnalytics />
                </Layout>
              </PrivateRoute>
            }
          />

          <Route
            path="/financial"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <FinancialStatus />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/backups"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <BackupManagement />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <AdminDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          
          <Route
            path="/response-times"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <ResponseTimes />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/warnings"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Warnings />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Messages />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <Payments />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/employee/:userId"
            element={
              <PrivateRoute>
                <Layout user={user}>
                  <EmployeeDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          </Routes>
          </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <InstallPWA />
      {isAuthenticated && <PushNotificationBanner />}
      </div>
    </ThemeProvider>
  );
}

export default App;
